/*
 * Copyright (c) Hilo 2020.
 * Author: fharold
 *
 * hilo-oauth-manager
 * UsersComponent.tsx
 */

import React from 'react';
import User from "../../models/User";
import UserService, {UserListener} from "../../services/UserService";
import UserAdapter, {UserAdapterListener} from "../adapter/UserAdapter";
import ReactDOM from "react-dom";
import UIAlert from "../../tools/view/toast/UIAlert";
import UIToast from "../../tools/view/toast/UIToast";
import HiloReactComponent from "../../tools/view/layout/HiloReactComponent";
import PageTitle, {TitleTab} from "../../tools/view/layout/PageTitle";
import {GroupAction} from "../../tools/list/ListToolbar";
import FormModal from "../../tools/view/layout/FormModal";
import UserAddForm from "../../models/form/user/UserAddForm";
import UserDataProvider from "../../provider/UserDataProvider";
import {ListWatcher} from "../../tools/list/ListAdapter";
import List from "../../tools/list/List";
import PageComponent from "../../tools/view/layout/PageComponent";
import ClientService from "../../services/ClientService";
import NavigationService from "../../tools/service/NavigationService";
import HTTPService from "../../tools/service/HTTPService";

interface Props {
}

interface State {
    pageIndex: number;
    userDataProvider: UserDataProvider;
    userAdapter: UserAdapter;
}

export default class UsersPage extends HiloReactComponent<Props, State> implements UserListener, UserAdapterListener, ListWatcher {
    private readonly _userService : UserService;
    private readonly _clientService : ClientService;
    private readonly _navigationService : NavigationService;
    private readonly _httpService : HTTPService;

    public listeners: Array<Function> = [];
    private _modalContainer?: Element;
    private _userAddForm?: UserAddForm;

    constructor(props: Props, state: State) {
        super(props, state);

        this._userService = this._serviceRepository.resolve(UserService.id) as UserService;
        this._clientService = this._serviceRepository.resolve(ClientService.id) as ClientService;
        this._navigationService = this._serviceRepository.resolve(NavigationService.id) as NavigationService;
        this._httpService = this._serviceRepository.resolve(HTTPService.id) as HTTPService;

        const userDataProvider = new UserDataProvider(this._userService, this._httpService);

        this.state = {
            pageIndex: 0,
            userDataProvider: userDataProvider,
            userAdapter: new UserAdapter(
                this.t,
                userDataProvider,
                this)
        }
    }

    /**
     * componentDidMount
     */
    public componentDidMount() {
        super.componentDidMount();
        this._userService.addListener(this);

        this._modalContainer = document.querySelector('#modal-container') || undefined;

        this.state.userDataProvider.subscribe(this);

        this._initForm();
    }

    /**
     * _initForm
     * @private
     */
    private _initForm = async () => {
        this._userAddForm = new UserAddForm(await this._clientService.findAll())
    }

    /**
     * componentWillUnmount
     */
    public componentWillUnmount() {
        this.state.userDataProvider.unsubscribe(this);
        this._userService.removeListener(this)
        super.componentWillUnmount();
    }


    /**
     * currentUserHasBeenUpdated
     * @param user
     */
    public currentUserHasBeenUpdated(user: User | undefined): void {
    }

    /**
     * onTabChanged
     * @param id identifier of the selected tab
     */
    public onTabChanged = (id: number) => {
        this.listeners.forEach(listener => listener(id))
    };

    /**
     * removeUserFromUser
     * @param user
     * @return {Promise<void>}
     */
    public async removeUser(user: User) {
        const {t} = this;

        const confirm = await UIAlert.default.show(t, t.UserComponentRemoveUser);

        if (!confirm) {
            return
        }

        //TODO : remove user

        UIToast.default.show(this.t.UserComponentRemoveUserConfirm);
        this.state.userDataProvider.load();
    }

    /**
     * rowSelected
     * @param obj
     */
    public rowSelected(obj: User): void {
        this._navigationService.load(`/user/${obj.id}`)
    }

    /**
     * _userList
     * @returns {any}
     * @private
     */
    private _userList = () => {
        const {userAdapter, userDataProvider} = this.state;

        return <List adapter={userAdapter} dataProvider={userDataProvider} onAddClick={this._displayAddUserModal}/>
    };

    /**
     * _applyGroupAction
     * @private
     */
    private _applyGroupAction = (ids: Array<string>, action: GroupAction) => {

    }

    /**
     * _saveUser
     * @param dto
     * @private
     */
    private _saveUser = async (dto: FormData) => {
        try {
            await this._userService.createUser(dto);
            if (this._userAddForm) this._userAddForm.statusCode = 201;
            UIToast.default.show(this.t.userAdded);
            this.state.userDataProvider.load();
        } catch (e) {
            if (this._userAddForm) {
                this._userAddForm.statusCode = e.status
            }
        }
    };

    /**
     * _displayAddUserModal
     * @private
     */
    private _displayAddUserModal = async () => {
        if (!this._modalContainer || !this._userAddForm) return;

        const modal = <FormModal
            emptyForm={this._userAddForm}
            title={t => t.newUser}
            onSaveClicked={this._saveUser}
        />;

        ReactDOM.render(modal, this._modalContainer);
    }

    /**
     * onUpdate
     */
    public onUpdate() {
        this.forceUpdate();
    }

    /**
     * render
     */
    public render(): JSX.Element {
        const titleTabs: TitleTab[] = [
            {title: t => t.list, content: <this._userList/>}
        ];

        return <PageComponent>
            <PageTitle title={t => t.usersAndAccess} tabs={titleTabs}
                       onTabSelected={index => this.setState({pageIndex: index})}/>
            {titleTabs[this.state.pageIndex].content}
        </PageComponent>
    }
}